// src/components/SendUsdtInternal.js
import React, { useState, useEffect } from "react";

const RECENT_EMAILS_KEY = "usdt_recent_emails";
const MAX_RECENTS = 5;

const BackChevron = ({ onClick }) => (
  <button
    onClick={onClick}
    className="flex items-center justify-center rounded-full p-1.5 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
    aria-label="Back"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M19 12H5" />
      <path d="M12 19l-7-7 7-7" />
    </svg>
  </button>
);

/*
 * Internal (Metisel user → Metisel user) sends are free — no
 * platform fee transfer, unlike the on-chain UsdtSend flow. This
 * mirrors what the backend's usdt:send-internal handler actually
 * does: it decrypts the sender's own custodial key, resolves ATAs,
 * builds a single sender -> recipient transfer instruction (plus
 * recipient-ATA creation if needed), signs, and broadcasts — all
 * server-side. The frontend just sends { toEmail, amount }.
 */
export default function SendUsdtInternal({
  wallet,
  usdtSocket,
  available = 0,
  onBack,
  mobileView = false,
  onSendSuccess,
  onSendError,
}) {
  /*
   * view: "send-input" | "send-confirm" | "send-success"
   * Mirrors BuyUswd's view-based flow: fill the form, preview/confirm
   * the details, then land on a success screen once the socket
   * confirms the transfer.
   */
  const [view, setView] = useState("send-input");
  const [toEmail, setToEmail] = useState("");
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [recentEmails, setRecentEmails] = useState([]);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");

  const [txInfo, setTxInfo] = useState(null); // { toEmail, amount }

  // What we last sent to the backend — used as a fallback for the
  // success screen if the success payload doesn't echo it back.
  const [pendingInfo, setPendingInfo] = useState(null);

  /* ===============================
     LOAD RECENT EMAILS
  =============================== */
  useEffect(() => {
    try {
      const stored = JSON.parse(
        localStorage.getItem(RECENT_EMAILS_KEY) || "[]"
      );
      if (Array.isArray(stored)) setRecentEmails(stored);
    } catch {
      setRecentEmails([]);
    }
  }, []);

  /* ===============================
     SUCCESS / ERROR LISTENERS
  =============================== */
  useEffect(() => {

    if (!onSendSuccess || !onSendError) return;

    onSendSuccess.current = (payload) => {
      setLoading(false);

      setTxInfo({ ...pendingInfo, ...(payload || {}) });

      // Clear the form only after a confirmed success
      setAmount("");
      setToEmail("");
      setErrors({});
      setFormError("");

      setView("send-success");
    };

    onSendError.current = (payload) => {
      setLoading(false);

      setFormError(
        payload?.message ||
        "USDT transfer failed"
      );
    };

    return () => {
      onSendSuccess.current = null;
      onSendError.current = null;
    };

  }, [onSendSuccess, onSendError, pendingInfo]);

  const saveRecentEmail = (email) => {
    const next = [
      email,
      ...recentEmails.filter((e) => e !== email),
    ].slice(0, MAX_RECENTS);

    setRecentEmails(next);
    localStorage.setItem(RECENT_EMAILS_KEY, JSON.stringify(next));
  };

  const handleMax = () => {
    if (available > 0) {
      setAmount(Number(available).toFixed(2));
      setErrors((e) => ({ ...e, amount: null }));
    }
  };

  const validate = () => {
    const nextErrors = {};
    const value = Number(amount);

    if (!toEmail.trim()) {
      nextErrors.toEmail = "Receiver email is required";
    }

    if (!value || value <= 0) {
      nextErrors.amount = "Enter a valid amount";
    }

    if (value > 0 && value > Number(available)) {
      nextErrors.amount = "Insufficient available USDT balance";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handlePreview = () => {
    setFormError("");

    if (!wallet?.chatId) {
      setFormError("Wallet not ready");
      return;
    }

    if (!validate()) return;

    setView("send-confirm");
  };

  const handleBackToInput = () => {
    setFormError("");
    setView("send-input");
  };

  const handleConfirmSend = () => {
    setFormError("");

    // Re-validate in case the balance or inputs shifted while reviewing.
    if (!validate()) {
      setView("send-input");
      return;
    }

    const numericAmount = Number(amount);
    const email = toEmail.trim();

    setLoading(true);
    setPendingInfo({ toEmail: email, amount: numericAmount });

    /*
     * Nothing to build or sign client-side anymore — the backend
     * resolves the recipient by email, decrypts the sender's own
     * custodial key, and builds + signs + broadcasts the transfer
     * itself. We just emit the request and wait for
     * usdt:send-success / usdt:send-internal:error via the socket
     * listeners registered above.
     */
    usdtSocket.sendUsdtInternal({
      toEmail: email,
      amount: numericAmount,
    });

    saveRecentEmail(email);

    /*
     * Do NOT clear the form here.
     * Wait for onSendSuccess or onSendError so the user
     * doesn't lose the entered email and amount if the
     * transfer fails.
     */
  };

  const handleSendAnother = () => {
    setView("send-input");
    setTxInfo(null);
    setPendingInfo(null);
    setToEmail("");
    setAmount("");
    setFormError("");
    setErrors({});
  };

  /* ============================================================
     VIEW: send-success
  ============================================================ */
  if (view === "send-success") {
    return (
      <div className="flex h-full w-full flex-col bg-white dark:bg-neutral-900">
        {/* Header */}
        <div className="border-b border-neutral-200 p-4 dark:border-neutral-700">
          <div className="flex items-center gap-3">
            {onBack && <BackChevron onClick={onBack} />}
            <h2 className="text-base font-semibold text-orange-400">
              Send USDT (Internal)
            </h2>
          </div>
        </div>

        {/* Success body */}
        <div className="flex flex-1 flex-col items-center justify-center gap-6 px-6 py-8 text-center">
          {/* Checkmark icon */}
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
            <svg
              className="h-10 w-10 text-green-600 dark:text-green-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>

          <div>
            <p className="text-xl font-bold text-neutral-900 dark:text-white">
              USDT Sent!
            </p>
            <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
              Your transfer was submitted successfully.
            </p>
          </div>

          {/* Transaction details */}
          <div className="w-full rounded-xl border border-neutral-200 bg-neutral-50 p-4 text-left text-sm dark:border-neutral-700 dark:bg-neutral-800">
            {txInfo?.toEmail && (
              <div className="flex justify-between py-1">
                <span className="text-neutral-500 dark:text-neutral-400">Sent to</span>
                <strong className="text-neutral-900 dark:text-white break-all">
                  {txInfo.toEmail}
                </strong>
              </div>
            )}
            {txInfo?.amount !== undefined && (
              <div className="flex justify-between py-1">
                <span className="text-neutral-500 dark:text-neutral-400">Amount sent</span>
                <strong className="text-neutral-900 dark:text-white">
                  {Number(txInfo.amount).toFixed(2)} USDT
                </strong>
              </div>
            )}
            <div className="flex justify-between py-1">
              <span className="text-neutral-500 dark:text-neutral-400">Network fee</span>
              <strong className="text-green-600 dark:text-green-400">
                Free
              </strong>
            </div>
            <div className="flex justify-between py-1">
              <span className="text-neutral-500 dark:text-neutral-400">New balance</span>
              <strong className="text-green-600 dark:text-green-400">
                {Number(available).toFixed(2)} USDT
              </strong>
            </div>
          </div>

          {/* Actions */}
          <div className="flex w-full flex-col gap-3">
            <button
              onClick={handleSendAnother}
              className="
                w-full rounded-lg bg-orange-400 px-4 py-2.5
                text-sm font-semibold text-white
                hover:bg-orange-500
              "
            >
              Send Another
            </button>
            <button
              onClick={onBack}
              className="
                w-full rounded-lg border px-4 py-2.5
                text-sm font-semibold
                border-neutral-300 text-neutral-700
                hover:bg-neutral-100
                dark:border-neutral-600 dark:text-neutral-200 dark:hover:bg-neutral-800
              "
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ============================================================
     VIEW: send-confirm
  ============================================================ */
  if (view === "send-confirm") {
    const numeric = Number(amount);
    const afterSend = Number((Number(available) - numeric).toFixed(2));

    return (
      <div className="flex h-full w-full flex-col bg-white dark:bg-neutral-900">
        <div className="border-b border-neutral-200 p-4 dark:border-neutral-700">
          <div className="flex items-center gap-3">
            <BackChevron onClick={handleBackToInput} />
            <h2 className="text-base font-semibold text-orange-400">
              Confirm Transfer
            </h2>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4">
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            Please review the details below before confirming.
          </p>

          <div className="rounded-xl border bg-neutral-50 dark:border-neutral-700
                          dark:bg-neutral-800 divide-y dark:divide-neutral-700">
            <div className="flex justify-between gap-4 px-4 py-3 text-sm">
              <span className="shrink-0 text-neutral-500 dark:text-neutral-400">Sending to</span>
              <span className="break-all text-right text-neutral-700 dark:text-neutral-200">
                {toEmail.trim()}
              </span>
            </div>
            <div className="flex justify-between px-4 py-3 text-sm">
              <span className="text-neutral-500 dark:text-neutral-400">Amount</span>
              <strong className="text-orange-500">{numeric.toFixed(2)} USDT</strong>
            </div>
            <div className="flex justify-between px-4 py-3 text-sm">
              <span className="text-neutral-500 dark:text-neutral-400">Network fee</span>
              <span className="font-medium text-green-600 dark:text-green-400">Free</span>
            </div>
            <div className="flex justify-between px-4 py-3 text-sm font-semibold">
              <span className="text-neutral-700 dark:text-neutral-200">Total to deduct</span>
              <span className="text-orange-500">{numeric.toFixed(2)} USDT</span>
            </div>
            <div className="flex justify-between px-4 py-3 text-sm">
              <span className="text-neutral-500 dark:text-neutral-400">USDT balance after</span>
              <span className="font-semibold text-neutral-900 dark:text-white">
                {afterSend.toFixed(2)} USDT
              </span>
            </div>
          </div>

          <div className="rounded-xl bg-amber-50 border border-amber-200
                          dark:bg-amber-950 dark:border-amber-800 px-4 py-3">
            <p className="text-xs text-amber-700 dark:text-amber-300">
              This action cannot be undone. Double-check the recipient's email before confirming.
            </p>
          </div>

          {formError && (
            <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400">
              {formError}
            </div>
          )}
        </div>

        <div className="border-t border-neutral-200 px-6 py-4 dark:border-neutral-700 flex gap-3">
          <button
            onClick={handleBackToInput}
            disabled={loading}
            className="flex-1 rounded-lg border py-2.5 text-sm font-semibold
                       dark:border-neutral-600 hover:bg-neutral-50
                       dark:hover:bg-neutral-800 transition-colors disabled:opacity-40"
          >
            Edit
          </button>
          <button
            onClick={handleConfirmSend}
            disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-orange-400
                       py-2.5 text-sm font-semibold text-white disabled:opacity-40
                       hover:bg-orange-500 transition-colors"
          >
            {loading ? (
              <>
                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
                Sending…
              </>
            ) : (
              "Confirm & Send"
            )}
          </button>
        </div>
      </div>
    );
  }

  /* ============================================================
     VIEW: send-input
  ============================================================ */
  return (
    <div className="flex h-full w-full flex-col bg-white dark:bg-neutral-900">
      {/* ================= HEADER ================= */}
      <div className="border-b border-neutral-200 p-4 dark:border-neutral-700">
        <div className="flex items-center gap-3">
          {onBack && <BackChevron onClick={onBack} />}

          <h2 className="text-base font-semibold text-orange-400">
            Send USDT (Internal)
          </h2>
        </div>
      </div>

      {/* ================= CONTENT ================= */}
      <div className="flex flex-col gap-6 p-6">
        {/* ===== BALANCE CARD ===== */}
        <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4 dark:border-neutral-700 dark:bg-neutral-800">
          <div className="text-xs text-neutral-500 dark:text-neutral-400">
            Available Balance
          </div>
          <div className="mt-1 text-xl font-bold text-green-600 dark:text-green-400">
            {Number(available).toFixed(2)}{" "}
            <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
              USDT
            </span>
          </div>
        </div>

        {/* ===== FORM ===== */}
        <div className="flex flex-col gap-4">

          {/* Receiver Email */}
          <div>
            <label className="mb-1 block text-xs font-semibold text-neutral-700 dark:text-white">
              Receiver Email
            </label>

            <input
              type="email"
              list="usdt-recent-emails"
              placeholder="user@example.com"
              value={toEmail}
              onChange={(e) => {
                setToEmail(e.target.value);
                setErrors((er) => ({ ...er, toEmail: null }));
              }}
              className={`
                w-full rounded-lg border px-4 py-2.5 text-sm
                bg-white text-neutral-900
                focus:outline-none focus:ring-2
                dark:bg-neutral-900 dark:text-white
                ${errors.toEmail
                  ? "border-red-400 focus:ring-red-400"
                  : "border-neutral-300 focus:ring-orange-400 dark:border-neutral-600"}
              `}
            />

            <datalist id="usdt-recent-emails">
              {recentEmails.map((email) => (
                <option key={email} value={email} />
              ))}
            </datalist>

            {errors.toEmail && (
              <div className="mt-1 text-xs text-red-500">
                {errors.toEmail}
              </div>
            )}
          </div>

          {/* Amount + MAX */}
          <div>
            <label className="mb-1 block text-xs font-semibold text-neutral-700 dark:text-white">
              Amount (USDT)
            </label>

            <div className="relative">
              <input
                type="number"
                placeholder="0.00"
                value={amount}
                onChange={(e) => {
                  setAmount(e.target.value);
                  setErrors((er) => ({ ...er, amount: null }));
                }}
                min="0"
                step="0.01"
                className={`
                  w-full rounded-lg border px-4 py-2.5 pr-16 text-sm
                  bg-white text-neutral-900
                  focus:outline-none focus:ring-2
                  dark:bg-neutral-900 dark:text-white
                  ${errors.amount
                    ? "border-red-400 focus:ring-red-400"
                    : "border-neutral-300 focus:ring-green-500 dark:border-neutral-600"}
                `}
              />

              <button
                type="button"
                onClick={handleMax}
                className="
                  absolute right-2 top-1/2 -translate-y-1/2
                  rounded-md px-2 py-1 text-xs font-semibold
                  text-green-600 hover:bg-green-50
                  dark:text-green-400 dark:hover:bg-neutral-700
                "
              >
                MAX
              </button>
            </div>

            {errors.amount && (
              <div className="mt-1 text-xs text-red-500">
                {errors.amount}
              </div>
            )}
          </div>

          {formError && (
            <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400">
              {formError}
            </div>
          )}

          {/* Preview button */}
          <button
            disabled={loading}
            onClick={handlePreview}
            className="
              mt-2 flex items-center justify-center
              rounded-lg px-4 py-2.5 text-sm font-semibold
              bg-orange-400 text-white
              hover:bg-orange-500
              disabled:opacity-50 disabled:cursor-not-allowed
            "
          >
            {Number(amount) > 0
              ? `Preview: Send ${Number(amount).toFixed(2)} USDT`
              : "Send USDT"}
          </button>
        </div>

        {/* ===== FOOT NOTE ===== */}
        <div className="text-xs text-neutral-500 dark:text-neutral-400">
          Transfers are instant and free between Metisel users.
        </div>
      </div>
    </div>
  );
}